/*
* Copyright Gregory Coburn 2020-2026, All Rights Reserved, See license for further details
*/
import { Injectable, inject } from '@angular/core';
import { Observable, ReplaySubject, of } from 'rxjs';

import { catchError, first, map, tap } from 'rxjs/operators';

import { HttpClient } from '@angular/common/http';

import { AbstractHttpService } from '@src/app/shared/abstract-http.service';
import { TenantService } from '@src/app/shared/tenant.service';
import { MessageService, MsgDef } from '@src/app/shared/message.service';
import { Router } from '@angular/router';
import { Team } from '@src/app/model/team';
import { Role } from '@src/app/model/role';
import { NavRoute } from '@src/app/shared/NavRoute';
import { NavItem } from '@src/app/model/sys/nav-item';
import { TermsOfServiceComponent } from '@src/app/shared/legal/terms-of-service/terms-of-service.component';
import { User } from '@src/app/model/user';
import { PersonUnitRole } from '@src/app/model/person-unit-role';
import { Forum, ForumPrivName } from '@src/app/model/forum';
import { DefaultBCode, DefaultBCodeType } from '@src/app/model/bcode';
import { TenantId } from '@src/app/model/abstract-object';
import { ConfirmDialogComponent, ConfirmDialogService } from '@src/app/shared/dialogs/confirmDialog';
import { OurAppTrackerService } from '@src/app/shared/our-app-tracker-service';
import { MatDialogRef } from '@angular/material/dialog';
import { buildVersion } from '@src/environments/version';

@Injectable({
    providedIn: 'root'
})
export class CurrentUserService extends AbstractHttpService {
    protected http = inject(HttpClient);
    protected messageService = inject(MessageService);
    private cds = inject(ConfirmDialogService);
    private ourAppTracker = inject(OurAppTrackerService);
    protected router = inject(Router);


    currentUser: User; // = new User();
    currentTeam: Team;
    currentRole: Role;

    gotUser = false;
    gettingUser = false;

    thisInstance = Math.random();
    alertOpen: MatDialogRef<ConfirmDialogComponent>

    static readonly returnMe = "returnMeTo";

    protected cache;
    protected typeString = 'CurrentUser';
    protected get baseUrl() {
        return this.getAjaxPath('user');
    }

    //private subjectUser = new BehaviorSubject<User | null>(null);
    private subjectUser = new ReplaySubject<User | null>(1);

    private checkLoaded() {
        if (!this.currentUser || !this.gotUser || this.gettingUser) {
            console.error('Getting forums before user retrieved');
        }
    }

    getForums(withPriv: ForumPrivName = null) {
        this.checkLoaded()
        return this.currentUser.forumRoles.reduce((unique, current) => {
            if (unique.find(o => o.id === current.forumId && o.teamId === current.teamId)) {
                //already have it;
            } else {
                if (current.teamId === this.currentTeam.id && (withPriv === null || current[withPriv])) {
                    //console.log({ forum: current.forumName, withPriv, current});
                    unique.push({ id: current.forumId, name: current.forumName, teamId: current.teamId, visibility: current.visibility } as Forum);
                }
            }
            return unique;
        }, [] as Forum[]);
    }

    getActivePositions() {
        this.checkLoaded()
        return this.currentUser.activePositions.reduce((unique, current) => {
            if (unique.find(o => o.roleId === current.roleId && o.teamId === current.teamId)) {
                //already fo it;
            } else {
                unique.push(current);
            }
            return unique;
        }, [] as PersonUnitRole[]);
    }

    private initNavItem(ni: NavItem) {
        if (ni.model && ni.model !== null) {
            const nr = NavRoute.getRoute(ni.url);
            if (nr && this.currentUser && this.currentUser.currentTeam) {
                ni.views = nr.getViews(this.currentUser);
                ni.charts = nr.getCharts();
            }
        }

        if (ni.children) {
            for (const nic of ni.children.filter(nicf => nicf.model)) {
                this.initNavItem(nic);
            }
        }
    }

    pathToModel(modelToPath: Record<string, string>) {
        const pathToModel = {};
        for (const [key, value] of Object.entries(modelToPath)) {
            pathToModel[value] = key;
        }
        return pathToModel;
    }

    private setCurrentUser(currentUser: User) {
        this.currentUser = currentUser;
        this.currentTeam = currentUser.currentTeam;
        this.tenantSvc.setCurrentTenantId(currentUser.current_team_id);

        if (!this.currentUser.navItems) {
            this.currentUser.navItems = [];
        }
        for (const ni of this.currentUser.navItems) {
            this.initNavItem(ni);
        }
        this.currentUser.pathToModel = this.pathToModel(currentUser.modelToPath);

        this.gotUser = true;
        this.gettingUser = false;

        if (currentUser.inAgency) {
            this.setAgentPosition();
        }        

        this.subjectUser.next(this.currentUser);
        //this.postLoginMessage();
    }

    httpGetCurrentUser(goto: string = null) {
        this.gettingUser = true;
        this.gotUser = false;

        this.http.get<User>(TenantService.bareAjaxPath + 'currentUser', this.httpOptions).pipe(first()).subscribe(currentUser => {

            TermsOfServiceComponent.checkTermsOfService(currentUser);
            if (currentUser && typeof currentUser.currentVersion === 'string') {
                if (currentUser.currentVersion.trim() !== buildVersion) {
                    this.cds.alert('Version Update', 'There has been a new software version released, we will need to refresh your browser', () => {
                        window.location.reload();
                    });
                }
            }
            this.setCurrentUser(currentUser);
            if (goto) {
                this.router.navigateByUrl(goto)
            } else {
                // goto lost by oAuth... Let's try get there anyway...
                const returnMePath = window.localStorage.getItem(CurrentUserService.returnMe);
                if (returnMePath) {
                    const retO = JSON.parse(returnMePath);
                    if (retO['time'] && retO['time'] > new Date().getTime() - (60 * 15 * 1000)) {
                        window.localStorage.removeItem(CurrentUserService.returnMe);
                        this.router.navigate([retO['path']]);
                    }
                }
            }
        }, error => {
            this.gettingUser = false;
            this.subjectUser.next(null);
            if (error.status === 503) {
                window.location.href = "https://clgsystems.ie/maintenance-mode?site=" + window.location.hostname + "&url=" + window.location.href;
            } else if (error.status !== 401) {
                console.error('Error occured retrieving current user details', error);
            }

        });
    }

    getCurrentUserCheck() {
        return this.http.get(TenantService.bareAjaxPath + 'currentUser/check');
    }

    logoutCurrentUser() {
        this.http.post('/api/logout', {}).subscribe(() => {
            this.httpGetCurrentUser();
            // TODO this.gotUser = false;
            this.router.navigate(['/']);
            this.ourAppTracker.reloadPage();
        }, error => console.log('Logout Error', error));
    }

    switchTeamRole(pur: PersonUnitRole, stayOnPage?: boolean) {
         
        this.http.put(TenantService.bareAjaxPath + 'user/teams', { positionId: pur.id }).subscribe(
            (newPur: PersonUnitRole) => {
                console.log(newPur);                
                if (!stayOnPage) {
                    console.log('reloading');
                    this.ourAppTracker.reloadPage('/');
                } else {
                    this.ourAppTracker.reloadPage();
                }
            },
            error => {
                console.error('Failed to switch teams', error);
                const msg = $localize`Unable to switch to team ${pur?.team?.name} (${pur?.role?.name})`;
                this.messageService.show(new MsgDef(msg, 'fail'));
            }
        );
    }

    private setAgentPosition() {
        const cu = this.currentUser;
        const position = cu.activePositions.find(ap => ap.teamId === cu.current_team_id && ap.roleId === cu.currentRole.id);
        //window.localStorage.setItem('myAgentTeam', JSON.stringify({teamId: cu.current_team_id, positionId: position.id}));
        if (position) {
            window.localStorage.setItem('myAgentPosition', JSON.stringify(position));
        } else {
            console.error('Cannot set agent position', {cu, position})
            this.messageService.showErrorMessage('Was not able to setup the agent position');
        }
    }

    getAgentPosition() : PersonUnitRole | false{
        const e =  localStorage.getItem('myAgentPosition');
        if (e && e !== 'undefined') {
            return JSON.parse(e);
        }
        return false;
    }

    switchTenant(tenantId: TenantId) : Observable<User> {
        return this.http.put<User>(TenantService.bareAjaxPath + 'user/teams', { teamId: tenantId }).pipe(
            map( (currentUser: User) => {
                this.setCurrentUser(currentUser);
                return currentUser;
            }),
            catchError(this.handleOneError('switchingTenant to ' + tenantId, null)
        ));
    }

    /**
     * Emits the current team's default budget codes once, then completes - safe inside forkJoin.
     * Held on the team object, so a tenant switch fetches the new team's codes. A tenant switch
     * while the request is in flight would hand back the previous team's codes, so that throws.
     */
    getDefaultBCodes(): Observable<DefaultBCode[]> {
        if (this.currentTeam?.defaultBCodes) {
            return of(this.assertCurrentTeams(this.currentTeam.defaultBCodes));
        }
        return this.http.get<DefaultBCode[]>(TenantService.ajaxPath + 'defaultBCodes').pipe(
            map(dbcs => this.assertCurrentTeams(dbcs)),
            tap(dbcs => this.currentTeam.defaultBCodes = dbcs)
        );
    }

    private assertCurrentTeams(dbcs: DefaultBCode[]): DefaultBCode[] {
        const strayTeamId = dbcs.find(o => o.teamId !== this.currentTeam?.id)?.teamId;
        if (strayTeamId) {
            throw new Error(`Default budget codes for team ${strayTeamId} `
                + `while current team is ${this.currentTeam?.id}`);
        }
        return dbcs;
    }

    getDefaultBCode(type: DefaultBCodeType) {
        if (!this.currentTeam.defaultBCodes) {
            console.error('No default BCodes, Are youu sure getDefaultBCodes was exceuted to get them?');
        }
        return this.currentTeam.defaultBCodes.find(o => o.type === type);
    }

    public hasUber() {
        const uberEmails = ['greg@coburn.ie', 'kieron.connolly@gmail.com'];
        // admin@ouromc.ie is uber only in non-production environments
        if (window.location.hostname !== 'manage.ouromc.ie') {
            uberEmails.push('admin@ouromc.ie');
        }
        return uberEmails.includes(this.currentUser?.email ?? '');
    }

    getCurrentUser(): Observable<User | null> {
        return this.subjectUser.asObservable();
    }

    gettingCurrentUserNow() {
        this.gettingUser = true;
    }

    static returnMeTo(to: string) {
        window.localStorage.setItem(CurrentUserService.returnMe, JSON.stringify({ path: to, time: new Date().getTime() }));
    }

}
